// layout/Header/index.js
import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, HStack, Text, useTheme } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import CustomLink from '../../components/CustomLink/CustomLink'
import { useLabels } from '../../utils/hooks/useLabels/useLabels'
import MenuLink from '../../components/MenuLink/MenuLink'
import { REPOSITORY } from '../../api/repository/Repository'
import Profile from '../../components/Profile/Profile'
import { useLoadingContext } from '../../utils/hooks/Context/LoadingContext'
import { useRequestStatusContext } from '../../utils/hooks/Context/RequestStatusContext'
import SurveyModalRefill from '../../components/SurveyRefill/SurveyModalRefill'
import { useCompanySurvey } from '../../utils/hooks/useCompanySurvey'
import {
  coerceCompleted,
  readAplica,
} from '../../utils/hooks/useSurveyModalRefillGate'
import { submitSurveyAnswers } from '../../utils/surveyApi'
import { surveyHero } from '../../utils/surveys'
import heroImg from '../../assets/images/ami-survey-refill-hero.webp'
import api from '../../api/api'
import { endpoints } from '../../api/apiCommons'

const menuItems = ['refills', 'account', 'company']

function Header() {
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()
  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()

  const navigation = useNavigate()
  const { pathname } = useLocation()
  const [portal, setPortal] = useState(false)
  const [itemSelected, setItemSelected] = useState(() => {
    if (pathname === '/') return 0
    return (
      menuItems.findIndex((element) => {
        const paths = pathname.split('/')
        return paths[1] === element
      }) + 1
    )
  })

  const { sEmpresa } = REPOSITORY.local.company.getCompanyData()
  const { texts } = useLabels()
  const companyId = String(sEmpresa ?? '')

  // Gate modal
  const { markCompleted, surveyCode, hero, answered } = useCompanySurvey({
    companyId,
  })

  // Selecciona al momento de navegar: también cubre el primer clic durante la carga.
  const answeredRef = useRef(answered)
  answeredRef.current = answered
  async function selectPendingSurvey(cId) {
    if (!cId || answeredRef.current) return null
    const token = localStorage.getItem('token') || ''
    const { data: body } = await api.get(endpoints.surveySeleccionar, {
      params: { sEmpresa: cId, _ts: Date.now() },
      headers: { token, 'Cache-Control': 'no-cache' },
    })
    const selection = body?.data || body
    const code = selection?.CodigoSurvey
    if (!code) return null
    const { data: stateBody } = await api.get(endpoints.surveyEstado, {
      params: { sEmpresa: cId, CodigoSurvey: code, _ts: Date.now() },
      headers: { token },
    })
    const state = stateBody?.data || stateBody
    return readAplica(state) === true && !coerceCompleted(state) ? code : null
  }

  // Estado del modal y navegación pendiente
  const [surveyOpen, setSurveyOpen] = useState(false)
  const [pendingHref, setPendingHref] = useState(null)
  const [activeSurveyCode, setActiveSurveyCode] = useState(null)

  const companyIdRef = useRef(companyId)
  useEffect(() => { companyIdRef.current = companyId }, [companyId])

  const requestRefillsNavigation = async (path) => {
    // Fallo de encuestas: se conserva el acceso a recargas.
    let code = null
    try { code = await selectPendingSurvey(companyIdRef.current) }
    catch (_) { code = null }
    if (!code) {
      navigation(path)
      return
    }
    setActiveSurveyCode(code)
    setPendingHref(path)
    setSurveyOpen(true)
  }

  const requestNavigationRef = useRef(requestRefillsNavigation)
  requestNavigationRef.current = requestRefillsNavigation

  useEffect(() => {
    const handler = (event) => {
      const path = event?.detail?.to
      if (typeof path === 'string' && /^\/refills(?:\/|$)/.test(path)) {
        requestNavigationRef.current(path)
      }
    }
    window.addEventListener('company-survey:navigate', handler)
    return () => window.removeEventListener('company-survey:navigate', handler)
  }, [])

  useEffect(() => {
    const labels = {
      [(texts.refills || '').toLowerCase()]: '/refills',
      [(texts.new_refill || '').toLowerCase()]: '/refills/new',
      [(texts.balance_discount || '').toLowerCase()]: '/refills/discount',
    }

    const getPathFromEventTarget = (target) => {
      const anchor = target?.closest?.('a')
      if (anchor) {
        let path = anchor.getAttribute('href') || ''
        if (!path && anchor.href) {
          try {
            path = new URL(anchor.href).pathname
          } catch {}
        }
        if (path) return path
      }
      const menuItem = target?.closest?.('[role="menuitem"]')
      if (menuItem) {
        const label = (menuItem.textContent || '').trim().toLowerCase()
        if (labels[label]) return labels[label]
      }
      const dataHrefEl = target?.closest?.('[data-href]')
      if (dataHrefEl) {
        const path = dataHrefEl.getAttribute('data-href')
        if (path) return path
      }
      return ''
    }

    const handleClick = async (e) => {
      const path = getPathFromEventTarget(e.target)
      if (path && (path === '/refills' || path.startsWith('/refills/'))) {
        e.preventDefault()
        e.stopPropagation()

        requestNavigationRef.current(path)
      }
    }

    const handleKeyDown = async (e) => {
      if (!(e.key === 'Enter' || e.key === ' ')) return
      const path = getPathFromEventTarget(e.target)
      if (path && (path === '/refills' || path.startsWith('/refills/'))) {
        e.preventDefault()
        e.stopPropagation()

        requestNavigationRef.current(path)
      }
    }

    document.addEventListener('click', handleClick, true)
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, texts])

  async function submitRefillSurvey(answers) {
    await submitSurveyAnswers({
      companyId,
      surveyCode: activeSurveyCode,
      answers,
    })
    // OK → marca completado (gate local)
    markCompleted()
  }

  const continueToRefills = () => {
    setSurveyOpen(false)
    handleChangeLink(1)
    const target = pendingHref || '/refills'
    navigation(target)
    setPendingHref(null)
  }

  const handleChangeLink = (item) => setItemSelected(item)

  useEffect(() => {
    if (sEmpresa)
      REPOSITORY.wsEmpresa.nuevoPortal
        .getFlag()
        .then((response) => {
          setPortal(response.data.bNuevoPortal)
        })
        .catch((error) => {
          console.log(error)
          setPortal(undefined)
        })
  }, [sEmpresa])

  const {
    colors: { primary, secondary },
  } = useTheme()

  const handleRefill = () => requestNavigationRef.current('/refills/new')

  const updateflagPortal = () => {
    handleOpenLoading()
    REPOSITORY.wsEmpresa.nuevoPortal
      .updateFlag(!portal)
      .then(() => {
        setPortal(!portal)
      })
      .catch((error) => {
        handleSetTitle(error.response?.data?.error, error.response?.status)
        handleChangeBody(
          <Button variant="primary" onClick={handleClose}>
            {error.status === 200 ? 'Continuar' : 'Contactar a soporte ahora'}
          </Button>,
        )
        handleOpen()
      })
      .finally(() => {
        handleCLoseLoading()
      })
  }

  const onSalaCuna = () => {
    handleOpenLoading()
    REPOSITORY.wsEmpresa.token
      .refreshToken()
      .then((success) => {
        const token = success.data.token
        // eslint-disable-next-line no-undef
        location.href = `${process.env.REACT_APP_EMPRESAS_REDIRECT}/loginp.aspx?t=${token}`
      })
      .catch(() => {
        handleCLoseLoading()
        alert('Error servicio token')
      })
  }

  return sEmpresa ? (
    <HStack
      position="fixed"
      zIndex={101}
      w="100%"
      h="92px"
      bg={primary.base}
      justifyContent="space-around"
      alignItems="center"
    >
      <HStack h="100%" w="50%" spacing="50px" justifyContent="space-between">
        <CustomLink to="/" onClick={() => handleChangeLink(0)}>
          {texts.dashboard}
        </CustomLink>

        <MenuLink
          showUnderLine={itemSelected === 1}
          onClick={() => handleChangeLink(1)}
          parentLink={{ to: '/refills', label: texts.refills }}
          links={[
            { to: 'new', label: texts.new_refill },
            { to: 'discount', label: texts.balance_discount },
          ]}
        />

        <SurveyModalRefill
          isOpen={surveyOpen}
          onClose={continueToRefills}
          onSubmitAnswers={submitRefillSurvey}
          companyId={companyId}
          surveyCode={activeSurveyCode || surveyCode || undefined}
          hero={{ imageUrl: heroImg, ...(activeSurveyCode ? (surveyHero[activeSurveyCode] || {}) : (hero || {})) }}
        />

        <MenuLink
          showUnderLine={itemSelected === 2}
          onClick={() => handleChangeLink(2)}
          parentLink={{ to: '/account', label: texts.accounts }}
          links={[
            { to: '', label: texts.employee_management },
            { to: 'create-account', label: texts.create_account },
            { to: 'account-management', label: texts.nn_account_management },
          ]}
        />
        <MenuLink
          showUnderLine={itemSelected === 3}
          onClick={() => handleChangeLink(3)}
          parentLink={{ to: '/company', label: texts.company }}
          links={[
            { to: '', label: texts.company_data },
            {
              to: 'branch-office-management',
              label: texts.branch_and_cost_center,
            },
            { to: 'account-status', label: texts.account_status },
            { to: 'reports', label: texts.management_reports },
          ]}
        />

        <Box>
          <Button
            bgColor={secondary.base}
            _hover={{ backgroundColor: primary.dark }}
            onClick={handleRefill}
            borderRadius="16px"
            w="134px"
            h="30px"
          >
            <Text
              color="#FFFFFF"
              fontSize="13px"
              fontWeight={600}
              lineHeight="22px"
            >
              {texts.recharge_balance}
            </Text>
          </Button>
        </Box>
      </HStack>

      <Box>
        <Profile
          portal={portal}
          onSalaCuna={onSalaCuna}
          setPortal={() => {
            if (portal === undefined)
              return alert('Ocurrió un error al obtener el flag del portal')
            updateflagPortal()
          }}
        />
      </Box>
    </HStack>
  ) : null
}

export default Header
